import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { saveAs } from 'file-saver'
import { generatePdf } from '@gocanvas/pdf-renderer'

import {
  studioSettingsActions,
  MAX_ZOOM,
  MIN_ZOOM,
  PDF_MODE,
  TEST_MODE,
  INTRO_MODE,
} from 'apps/studio/store/reducers/settings/studioSettings'

const { setZoom, setDownloadingTestPdf } = studioSettingsActions

import {
  appSettingsActions,
  saveApp,
  fetchAppData,
} from 'apps/formsCreationKit/store/reducers/settings/appSettings'

import { flashMessageSelector } from 'apps/formsCreationKit/store/selectors/settings'

const { updateFlashMessage, saveAndPublishLoaded, toggleModal } =
  appSettingsActions

import { updateOnSave } from 'apps/formsCreationKit/store/actions/sharedActions'

import {
  activeModeSelector,
  currentZoomSelector,
  downloadingTestPdfSelector,
  plgDemoSelector,
} from 'apps/studio/store/selectors/studioSettings'

import {
  pdfFileSelector,
  testModePdfSchemaSelector,
} from 'apps/studio/store/selectors/pdf'

import {
  appSelector,
  entitiesSelector,
  dataSourceContextParamsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'

import { dataSourceContextsSelector } from 'apps/formsCreationKit/store/selectors/dataSourceContexts'

import {
  unsavedChangesSelector,
  isFormSavingSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import { fontTypesSelector } from 'apps/studio/store/selectors/types'
import { useHoneyBadger } from 'apps/builder/lib/hooks/honeybadger'

import {
  ZoomInIcon,
  ZoomOutIcon,
  DownloadIcon,
  SaveIcon,
  PublishIcon,
} from '@gocanvas/gcds/icons'

const saveForm = async ({ formId, entities, dispatch, notify }) => {
  const { dataSourceContexts } = entities
  try {
    const { data } = unwrapResult(
      await dispatch(
        saveApp({
          id: formId,
          entities,
          dataSourceContexts,
          builder_version: 'studio',
        })
      )
    )

    if (data?.success === false) {
      const error = Error('Something went wrong')
      error.data = data
      throw error
    }

    const { form_id, app_version, app_name } = data

    dispatch(
      updateOnSave({
        id: form_id,
        version: app_version,
        name: app_name,
      })
    )

    history.replaceState(
      { form_id, type: 'studio-edit' },
      '',
      `/studio/${form_id}/edit`
    )

    dispatch(
      updateFlashMessage({
        message: `Saved ${app_name} - Version ${app_version}`,
        type: 'success',
      })
    )
    return form_id
  } catch (e) {
    const { message = 'Something went wrong.' } = e?.data || e || {}
    notify(e)
    dispatch(
      updateFlashMessage({
        message,
        type: 'error',
      })
    )
  }
}

const openPublishModal = async ({ formId, dispatch, notify }) => {
  try {
    unwrapResult(await dispatch(fetchAppData(formId)))
  } catch (e) {
    notify(e)
    dispatch(saveAndPublishLoaded(false))
    dispatch(
      toggleModal({
        activeModal: '',
      })
    )
    dispatch(
      updateFlashMessage({
        message: 'Something went wrong',
        type: 'error',
      })
    )
  }
}

const handleDownload = async (file, schema, fontTypes, dispatch, notify) => {
  dispatch(setDownloadingTestPdf({ downloadingTestPdf: true }))

  try {
    const pdfDoc = await fetch(file).then(res => res.arrayBuffer())
    const pdfBytes = await generatePdf(pdfDoc, schema, { fontTypes })
    const generatedPdf = new Blob([pdfBytes.buffer], {
      type: 'application/pdf',
    })
    saveAs(generatedPdf, 'generated.pdf')
  } catch (error) {
    notify(error)
    dispatch(
      updateFlashMessage({
        message: 'Something went wrong.',
        type: 'error',
      })
    )
  } finally {
    dispatch(setDownloadingTestPdf({ downloadingTestPdf: false }))
  }
}

const minZoomFactor = MIN_ZOOM * 100
const maxZoomFactor = MAX_ZOOM * 100

const ZoomControls = () => {
  const dispatch = useDispatch()
  const currentZoom = useSelector(currentZoomSelector)

  const currentZoomPercentage = Math.max(0, currentZoom * 100)

  const atMin = currentZoomPercentage <= minZoomFactor
  const atMax = currentZoomPercentage >= maxZoomFactor

  const increaseZoom = () => {
    if (currentZoomPercentage >= maxZoomFactor) return

    // Find the next multiple of 5 that's larger than the current value
    const nextMultiple = Math.min(
      Math.ceil((currentZoomPercentage + 0.1) / 5) * 5,
      maxZoomFactor
    )

    const newZoom = Math.min(MAX_ZOOM, nextMultiple / 100)

    if (isFinite(newZoom)) {
      dispatch(setZoom({ zoom: newZoom }))
    }
  }

  const decreaseZoom = () => {
    if (currentZoomPercentage <= minZoomFactor) return

    // Find the previous multiple of 5 that's smaller than the current value
    const prevMultiple = Math.max(
      Math.floor((currentZoomPercentage - 0.1) / 5) * 5,
      minZoomFactor
    )

    const newZoom = Math.max(MIN_ZOOM, prevMultiple / 100)

    if (isFinite(newZoom)) {
      dispatch(setZoom({ zoom: newZoom }))
    }
  }

  const displayZoomPercentage = Math.max(
    minZoomFactor,
    Math.round(currentZoomPercentage)
  )

  return (
    <div className="header-right--zoom-controls">
      <button
        className="gcds--button button--icon"
        type="button"
        disabled={atMin}
        onClick={decreaseZoom}
      >
        <ZoomOutIcon />
      </button>

      <span className="zoom-control--percentage">
        Zoom <b>{`${displayZoomPercentage}%`}</b>
      </span>
      <button
        className="gcds--button button--icon"
        type="button"
        disabled={atMax}
        onClick={increaseZoom}
      >
        <ZoomInIcon />
      </button>
    </div>
  )
}

const getSaveButtonText = (isSaving, flashMessage, form) => {
  if (isSaving) {
    return 'Saving...'
  } else if (
    !isSaving &&
    flashMessage.type === 'success' &&
    flashMessage.message === `Saved ${form.name} - Version ${form.version}`
  ) {
    return 'Saved!'
  } else {
    return 'Save'
  }
}

const isSaveEnabled = (formId, pdfData, unsavedChanges) => {
  return !formId && pdfData ? true : unsavedChanges
}

const isPublishEnabled = (formStatus, saveEnabled, formId, unsavedChanges) => {
  return (formStatus !== 'published' && formId) || unsavedChanges
}

export const HeaderOptionsRight = () => {
  const dispatch = useDispatch()
  const activeMode = useSelector(activeModeSelector)
  const plgDemo = useSelector(plgDemoSelector)
  const form = useSelector(appSelector)
  const unsavedChanges = useSelector(unsavedChangesSelector)
  const isSaving = useSelector(isFormSavingSelector)
  const pdfData = useSelector(pdfFileSelector)
  const entities = useSelector(entitiesSelector)
  const dataSourceContexts = useSelector(dataSourceContextsSelector)
  const dataSourceContextParams = useSelector(dataSourceContextParamsSelector)
  const fontTypes = useSelector(fontTypesSelector)
  const testModePdfSchema = useSelector(testModePdfSchemaSelector)
  const downloadingTestPdf = useSelector(downloadingTestPdfSelector)
  const flashMessage = useSelector(flashMessageSelector) || {}
  const { id: formId, status: formStatus } = form

  const saveEnabled = isSaveEnabled(formId, pdfData, unsavedChanges)
  const publishEnabled = isPublishEnabled(
    formStatus,
    saveEnabled,
    formId,
    unsavedChanges
  )

  const fullEntities = {
    ...entities,
    dataSourceContextParams,
    dataSourceContexts,
  }

  const [notify] = useHoneyBadger()
  const [saveButtonText, setSaveButtonText] = useState('Save')

  useEffect(() => {
    const saveText = getSaveButtonText(isSaving, flashMessage, form)
    setSaveButtonText(saveText)
  }, [isSaving, flashMessage])

  // add beforeunload event listener conditionally
  // based on unsavedChanges state; skip in PLG demo (no real data to lose)
  useEffect(() => {
    if (plgDemo) return

    const unsavedChangesCheck = e => {
      if (unsavedChanges) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes, exit anyways?'
        return e.returnValue
      }
    }

    if (unsavedChanges) {
      window.addEventListener('beforeunload', unsavedChangesCheck)
    }

    return () => {
      window.removeEventListener('beforeunload', unsavedChangesCheck)
    }
  }, [unsavedChanges, plgDemo])

  const handlePublishClick = async () => {
    let publishedFormId = formId
    if (unsavedChanges || !formId) {
      publishedFormId = await saveForm({
        formId,
        entities: fullEntities,
        dispatch,
        notify,
      })
    }
    openPublishModal({
      formId: publishedFormId,
      dispatch,
      notify,
    })
  }

  return (
    <div className="header-section header-right">
      {activeMode === PDF_MODE && <ZoomControls />}
      {activeMode === TEST_MODE && (
        <button
          className="gcds--button button--secondary"
          type="button"
          onClick={() =>
            handleDownload(
              pdfData,
              testModePdfSchema,
              fontTypes,
              dispatch,
              notify
            )
          }
          disabled={downloadingTestPdf}
        >
          <DownloadIcon className="icon--leading" />
          <div className="button--label">Download PDF</div>
        </button>
      )}
      {activeMode !== INTRO_MODE && (
        plgDemo ? (
          <button
            className="gcds--button button--primary"
            type="button"
            onClick={() =>
              dispatch(toggleModal({ activeModal: 'plgPurchase' }))
            }
          >
            <SaveIcon className="icon--leading" />
            <div className="button--label">Save</div>
          </button>
        ) : (
          <>
            <button
              className="gcds--button button--secondary"
              type="button"
              onClick={() =>
                saveForm({ formId, entities: fullEntities, dispatch, notify })
              }
              disabled={!saveEnabled || isSaving}
            >
              <SaveIcon className="icon--leading" />
              <div className="button--label">{saveButtonText}</div>
            </button>
            <button
              className="gcds--button button--primary"
              type="button"
              disabled={!publishEnabled}
              onClick={handlePublishClick}
            >
              <PublishIcon className="icon--leading" />
              <div className="button--label">Publish</div>
            </button>
          </>
        )
      )}
    </div>
  )
}
